import React, { useEffect, useMemo, useState } from "react";
import {
  Popover,
  Box,
  List,
  ListItemButton,
  Checkbox,
  InputBase,
  Button,
  Typography,
  Divider,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { FaSearch } from "react-icons/fa";
import TeamMembersFilterPane from "./TeamMembersFilterPane";

const FILTER_CHECKBOX_SIZE = 17;
const FILTER_CHECK_ICON_SIZE = 13;

const sameFilterValue = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
const hasFilterValue = (list, value) => list.some((v) => sameFilterValue(v, value));
const withoutFilterValue = (list, value) => list.filter((v) => !sameFilterValue(v, value));

const filterCheckboxIcon = (
  <Box
    sx={{
      width: FILTER_CHECKBOX_SIZE,
      height: FILTER_CHECKBOX_SIZE,
      borderRadius: "5px",
      border: "1.5px solid #cbd5e1",
      bgcolor: "#fff",
      flexShrink: 0,
    }}
  />
);

const filterCheckboxCheckedIcon = (
  <Box
    sx={{
      width: FILTER_CHECKBOX_SIZE,
      height: FILTER_CHECKBOX_SIZE,
      borderRadius: "5px",
      bgcolor: "#2563eb",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <CheckIcon sx={{ fontSize: FILTER_CHECK_ICON_SIZE, color: "#fff" }} />
  </Box>
);

/**
 * Reusable two-pane filter popover.
 *
 * Left rail = filter categories. Right pane = searchable, multi-select option
 * list for the active category. Selections stay in a local draft; the parent is
 * notified via `onSelectionChange` when the dialog opens or a left-rail tab
 * changes (for facet cascading), and via `onApply` when Apply/Clear all is used.
 *
 * @param {boolean}  open
 * @param {HTMLElement|null} anchorEl - element to anchor the popover to
 * @param {function} onClose
 * @param {Array}    categories         [{ key, label }]
 * @param {Object}   optionsByCategory  { [key]: [{ value, label, count? }] }
 * @param {Object}   initialSelected    { [key]: string[] }
 * @param {function} onApply            (selected: { [key]: string[] }) => void
 * @param {function} [onSelectionChange] (selected: { [key]: string[] }) => void
 */
const TaskFilterDialog = ({
  open,
  anchorEl,
  onClose,
  categories = [],
  optionsByCategory = {},
  initialSelected = {},
  onApply,
  onSelectionChange,
  teamMembersType = "me",
}) => {
  const [activeKey, setActiveKey] = useState(categories[0]?.key);
  const [selected, setSelected] = useState(initialSelected);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      setSelected(initialSelected);
      if (onSelectionChange) onSelectionChange(initialSelected);
      setActiveKey((prev) => prev || categories[0]?.key);
      setSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const activeOptions = optionsByCategory[activeKey] || [];

  const meOption = useMemo(() => {
    if (activeKey !== "assignee") return null;
    return activeOptions.find((o) => o.label === "Me") || null;
  }, [activeKey, activeOptions]);

  const visibleOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    const withoutMe = meOption
      ? activeOptions.filter((o) => o.value !== meOption.value)
      : activeOptions;
    if (!q) return withoutMe;
    return withoutMe.filter((o) => o.label.toLowerCase().includes(q));
  }, [activeOptions, search, meOption]);

  const showMeInHeader =
    Boolean(meOption) &&
    (!search.trim() || meOption.label.toLowerCase().includes(search.trim().toLowerCase()));

  const toggleOption = (value) => {
    setSelected((prev) => {
      const current = prev[activeKey] || [];
      let next;
      if (hasFilterValue(current, "all")) {
          // If "all" was selected, clicking a single item replaces "all" with just that item.
          next = [value];
      } else {
          next = hasFilterValue(current, value)
            ? withoutFilterValue(current, value)
            : [...current, value];
      }
      return { ...prev, [activeKey]: next };
    });
  };

  const handleSelectAll = () => {
    setSelected((prev) => {
      const current = prev[activeKey] || [];
      const allValues = activeOptions.map((o) => o.value);
      const isAllSelected =
        allValues.length > 0 && allValues.every((v) => hasFilterValue(current, v));
      
      const nextSelected = { ...prev };
      if (isAllSelected) {
        nextSelected[activeKey] = [];
      } else {
        nextSelected[activeKey] = allValues;
      }
      return nextSelected;
    });
  };

  const handleSelectAllTeamMembers = (ids) => {
    setSelected((prev) => {
      const nextSelected = { ...prev };
      const current = prev["teamMembers"] || [];
      
      // If we are passing exact "all" string from the new TeamMembersFilterPane
      if (ids.length === 1 && ids[0] === "all") {
        if (current.includes("all")) {
            nextSelected["teamMembers"] = [];
        } else {
            nextSelected["teamMembers"] = ["all"];
        }
      } else {
        // Fallback for any other select all behaviors
        const isAllSelected = ids.every(id => current.includes(id)) && ids.length > 0;
        if (isAllSelected) {
          nextSelected["teamMembers"] = current.filter(id => !ids.includes(id));
        } else {
          nextSelected["teamMembers"] = Array.from(new Set([...current, ...ids]));
        }
      }
      
      return nextSelected;
    });
  };

  const handleCategoryClick = (key) => {
    if (key === activeKey) return;
    if (onSelectionChange) onSelectionChange(selected);
    setActiveKey(key);
    setSearch("");
  };

  const countFor = (key) => (selected[key] || []).length;
  const totalSelected = categories.reduce((sum, c) => sum + countFor(c.key), 0);

  return (
    <Popover
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            width: 576,
            maxWidth: "calc(100vw - 24px)",
            mt: 0.6,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", height: 368 }}>
        {/* Category rail */}
        <Box
          sx={{
            width: 176,
            flexShrink: 0,
            bgcolor: "#f8fafc",
            borderRight: "1px solid #e8eaed",
            overflowY: "auto",
          }}
        >
          <List disablePadding>
            {categories.map((cat) => {
              const isActive = cat.key === activeKey;
              const n = countFor(cat.key);
              return (
                <ListItemButton
                  key={cat.key}
                  selected={isActive}
                  onClick={() => handleCategoryClick(cat.key)}
                  sx={{
                    py: 1.2,
                    px: 1.6,
                    "&.Mui-selected": { bgcolor: "transparent" },
                    "&.Mui-selected:hover": { bgcolor: "rgba(0,0,0,0.03)" },
                  }}
                >
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: "0.95rem",
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? "#2563eb" : "#334155",
                    }}
                  >
                    {cat.label}
                  </Typography>
                  {n > 0 && (
                    <Box
                      sx={{
                        minWidth: 16,
                        height: 16,
                        px: 0.6,
                        mr: 0.8,
                        borderRadius: "8px",
                        bgcolor: "#2563eb",
                        color: "#fff",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {n}
                    </Box>
                  )}
                  <Typography sx={{ color: "#9ca3af", fontSize: "1rem" }}>›</Typography>
                </ListItemButton>
              );
            })}
          </List>
        </Box>

        {/* Options pane */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {activeKey === "teamMembers" ? (
            <TeamMembersFilterPane
              selectedIds={selected[activeKey] || []}
              onToggle={toggleOption}
              onSelectAll={handleSelectAllTeamMembers}
              type={teamMembersType}
            />
          ) : (
            <>
              <Box sx={{ p: 1.6, pb: 0.8 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    border: "1px solid #2563eb",
                    borderRadius: "8px",
                    px: 1.2,
                    py: "6px",
                  }}
                >
                  <FaSearch color="#9ca3af" size={12} />
                  <InputBase
                    fullWidth
                    autoFocus
                    placeholder={`Search ${(categories.find((c) => c.key === activeKey)?.label || "").toLowerCase()}…`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                      fontSize: "0.95rem",
                      "& input": { py: 0 },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ flex: 1, overflowY: "auto", px: 1.6 }}>
                {visibleOptions.length === 0 &&
                !(activeKey === "assignee" || activeKey === "assigner") ? (
                  <Typography sx={{ color: "#9ca3af", fontSize: "0.9rem", py: 1.6 }}>
                    No options found.
                  </Typography>
                ) : (
                  <>
                    {(activeKey === "assignee" || activeKey === "assigner") && (
                      <Box
                        sx={{
                          borderBottom: "1px solid #f1f5f9",
                          mb: 1,
                        }}
                      >
                        <Box
                          onClick={handleSelectAll}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                            py: 0.6,
                            cursor: "pointer",
                            borderRadius: 1,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                          }}
                        >
                          <Checkbox
                            checked={
                              activeOptions.length > 0 &&
                              activeOptions.every((o) => hasFilterValue(selected[activeKey] || [], o.value))
                            }
                            icon={filterCheckboxIcon}
                            checkedIcon={filterCheckboxCheckedIcon}
                            disableRipple
                            sx={{
                              p: 0.5,
                              "&:hover": { bgcolor: "transparent" },
                            }}
                          />
                          <Typography sx={{ flex: 1, fontSize: "0.95rem", color: "#1f2937", fontWeight: 600 }}>
                            Select All
                          </Typography>
                        </Box>
                        {showMeInHeader && (
                          <Box
                            onClick={() => toggleOption(meOption.value)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.8,
                              py: 0.6,
                              cursor: "pointer",
                              borderRadius: 1,
                              "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                            }}
                          >
                            <Checkbox
                              checked={hasFilterValue(selected[activeKey] || [], meOption.value)}
                              icon={filterCheckboxIcon}
                              checkedIcon={filterCheckboxCheckedIcon}
                              disableRipple
                              sx={{
                                p: 0.5,
                                "&:hover": { bgcolor: "transparent" },
                              }}
                            />
                            <Typography sx={{ flex: 1, fontSize: "0.95rem", color: "#1f2937", fontWeight: 600 }}>
                              Me
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                    {visibleOptions.length === 0 ? (
                      !showMeInHeader && (
                        <Typography sx={{ color: "#9ca3af", fontSize: "0.9rem", py: 1.6 }}>
                          No options found.
                        </Typography>
                      )
                    ) : (
                      visibleOptions.map((opt) => {
                        const checked = hasFilterValue(selected[activeKey] || [], opt.value);
                        return (
                          <Box
                            key={opt.value}
                            onClick={() => toggleOption(opt.value)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.8,
                              py: 0.6,
                              cursor: "pointer",
                              borderRadius: 1,
                              "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                            }}
                          >
                            <Checkbox
                              checked={checked}
                              icon={filterCheckboxIcon}
                              checkedIcon={filterCheckboxCheckedIcon}
                              disableRipple
                              sx={{
                                p: 0.5,
                                "&:hover": { bgcolor: "transparent" },
                              }}
                            />
                            <Typography sx={{ flex: 1, fontSize: "0.95rem", color: "#1f2937" }}>
                              {opt.label}
                            </Typography>
                            {opt.count != null && (
                              <Typography sx={{ color: "#9ca3af", fontSize: "0.85rem" }}>
                                {opt.count} tasks
                              </Typography>
                            )}
                          </Box>
                        );
                      })
                    )}
                  </>
                )}
              </Box>
            </>
          )}

          <Divider />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, p: 1.6 }}>
            <Button
              onClick={() => {
                setSelected({});
                if (onApply) onApply({});
              }}
              disabled={totalSelected === 0}
              sx={{ textTransform: "none", color: "#6b7280", mr: "auto" }}
            >
              Clear all
            </Button>
            <Button
              onClick={onClose}
              variant="outlined"
              size="small"
              sx={{
                textTransform: "none",
                borderColor: "#d1d5db",
                color: "#374151",
                minWidth: 72,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => onApply && onApply(selected)}
              variant="contained"
              size="small"
              sx={{
                textTransform: "none",
                bgcolor: "#2563eb",
                minWidth: 72,
                "&:hover": { bgcolor: "#1d4ed8" },
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Box>
    </Popover>
  );
};
export default TaskFilterDialog;
